import {
  getDurationInDays,
  differenceInDaysFromCurrentDate,
  countNights,
} from '@airasia-common/libraries/datetime';
import { percentPromotion } from '@airasia-common/libraries/utilities/formatters';
import {
  FILTER_TO_HOTEL_SEARCH_RESULTS,
  ROOM_BED_TYPE,
  CANCELLATION_PENALTY_TYPES,
  BREAKFAST_PROVISION,
  WIFI_PROVISION,
  PAYMENT_METHOD_ECOMMERCE_GTM,
} from '@airasia-common/libraries/constants';
import { getGtmDataLayer } from '@airasia-common/libraries/factories/Analytics';
import {
  getCurrentDeviceAgent,
  ApiDeviceIds,
} from '@airasia-common/libraries/utilities/DeviceAgentHelper';
import {
  createDisplayAmenitiesWeb,
  createRoomLevelAmenitiesMobile,
  getMaxRateAmenities,
} from '@airasia-common/libraries/utilities/AmenityHelper';
import {
  GA_VALUES,
  GUEST_REQUIRED_FIELDS,
  GA_LABELS,
  GA_BOOKING_STATUS_MAPPING,
  GA_BOOKING_PENALTY_TYPE,
  GA_EVENTS,
} from '../../constants';
import { types } from './hotel.meta';

const HOTEL_TRACKING_CATEGORIES = {
  SCREEN_NAME: 'Hotel Select',
  heatmapv2: 'HOTEL|Hotel Select heatmap',
  LABEL: 'map_view',
  LOB: 'HOTEL',
};

const mapRoomclickProduct = (
  id,
  name,
  bookingLeadDays,
  passengers,
  AdultsAndChildren,
  starRating,
  guestRating,
  hotelTags,
  guestRatingCount,
  amenities
) => {
  try {
    return {
      id, // vendor ID
      name, // hotel name
      brand: 'Hotel SA', // LOB
      category: 'Hotel SA - Hotel',
      variant: '',
      position: 1, // position of the hotel in the list
      dimension8: bookingLeadDays,
      dimension9: passengers,
      dimension10: passengers,
      dimension71: Number(starRating), // hotel star rating
      dimension72: Number(guestRating),
      dimension79: hotelTags, // hotel label from left, right, top bottom separate with '|', if there is no label replaced with 0.
      dimension103: guestRatingCount,
      dimension106: AdultsAndChildren,
      dimension107: getDisplayedAmenitiesCode(amenities), //hotelAmenities
    };
  } catch (error) {
    console.error(error);
    return {};
  }
};
const mapClick = (filter, hotel, guests, checkedIn, name, id) => {
  try {
    const {
      amenities,
      guestRating,
      guestRatingCount,
      starRating,
    } = hotel.hotel.hotelDetail;
    const bookingLeadDays = getBookingLeadDays(checkedIn);
    const passengers = getTotalPassengers(guests);
    const hotelTags = getHotelTags(hotel.hotel.hotelDetail);
    const AdultsAndChildren = `${guests?.adults || 0} | ${
      guests.children ? guests.children.length : 0
    }`;
    return mapRoomclickProduct(
      id,
      name,
      bookingLeadDays,
      passengers,
      AdultsAndChildren,
      starRating,
      guestRating,
      hotelTags,
      guestRatingCount,
      amenities
    );
  } catch (error) {
    console.error(error);
    return {};
  }
};
const hotelProductImpression = (
  id,
  name,
  AdultsAndChildren,
  amenities,
  countryCode,
  priceAfterDiscount,
  strikethroughPrice,
  posInList,
  starRating,
  guestRating,
  isLoggedInUser,
  guestRatingCount,
  hotelTags,
  bookingLeadDays,
  passengers,
  checkin,
  checkout,
  roomsCount,
  hotelLengthOfStay,
  deals
) => {
  try {
    return {
      id, // vendor ID
      name, // hotel name
      price: Number(priceAfterDiscount), // price per room per night after discount
      brand: 'Hotel SA', // LOB
      category: 'Hotel SA - Hotel',
      variant: deals,
      position: posInList, // position of the hotel in the list
      dimension8: bookingLeadDays,
      dimension9: passengers,
      dimension10: passengers,
      dimension60: countryCode,
      dimension67: checkin,
      dimension68: checkout,
      dimension69: hotelLengthOfStay,
      dimension70: roomsCount,
      dimension71: Number(starRating), // hotel star rating
      dimension72: Number(guestRating), // Hotel- Customer rating
      dimension79: hotelTags, // hotel label from left, right, top bottom separate with '|', if there is no label replaced with 0.
      dimension95: isLoggedInUser
        ? GA_VALUES.labels.logged_in
        : GA_VALUES.labels.non_loggedin,
      dimension103: guestRatingCount, //number or reviews
      dimension104: percentPromotion(
        Number(priceAfterDiscount),
        Number(strikethroughPrice)
      ), // Strikethrough price %
      dimension106: AdultsAndChildren,
      dimension107: getDisplayedAmenitiesCode(amenities),
      dimension110: Number(strikethroughPrice),
    };
  } catch (error) {
    console.error(error);
    return {};
  }
};

const hotelImpressionApp = (hotel, index, filter, isLoggedInUser) => {
  const {
    id,
    name,
    baseRate,
    strikethroughPrice,
    starRating,
    guestRating,
    guestRatingCount,
    amenities,
    paymentDiscountType,
  } = hotel;

  const deals = paymentDiscountType !== undefined ? paymentDiscountType : '';
  const { checkin, guests, checkout, countryCode, roomsCount } = filter;

  const hotelLengthOfStay = getDurationInDays(checkin, checkout);
  const bookingLeadDays = getBookingLeadDays(checkin);
  const passengers = getTotalPassengers(guests);
  const hotelTags = getHotelTags(hotel);
  const AdultsAndChildren = `${guests?.adults || 0} | ${
    guests.children ? guests.children.length : 0
  }`;

  return hotelProductImpression(
    id,
    name,
    AdultsAndChildren,
    amenities,
    countryCode,
    baseRate?.value,
    strikethroughPrice ? strikethroughPrice?.value : 0,
    index,
    starRating,
    guestRating,
    isLoggedInUser,
    guestRatingCount,
    hotelTags,
    bookingLeadDays,
    passengers,
    checkin,
    checkout,
    roomsCount,
    hotelLengthOfStay,
    deals
  );
};

const getHotelTags = hotel => {
  const { aboveNameLabels, leftPillTexts, rightPillTexts } = hotel;
  const tagLeft = leftPillTexts ? 1 : 0;
  const tagRight = rightPillTexts ? 1 : 0;
  const tagTop = aboveNameLabels ? 1 : 0;
  const tagBottom = 0; // Bottom label is empty for now
  return `${tagLeft}|${tagRight}|${tagTop}|${tagBottom}`;
};

const getFullAmenitiesCode = amenities => {
  const amenitiesCount = amenities && amenities.length;

  return amenitiesCount
    ? `{total:${amenitiesCount},${amenities.map(item => `id${item.id}:1`)}}`
    : `{total:${amenitiesCount}}`;
};

const getDisplayedAmenitiesCode = (amenities = []) => {
  const isWebView = getCurrentDeviceAgent().dId === ApiDeviceIds.isWebView;
  const maximumTrackingItems = isWebView ? 6 : 5;
  const trackedItems =
    amenities && amenities.length > maximumTrackingItems
      ? amenities.slice(0, maximumTrackingItems)
      : amenities;

  const trackedItemsCount = trackedItems.length;

  return trackedItemsCount
    ? `{total:${trackedItemsCount},${trackedItems.map(
        item => `id${item.id}:1`
      )}}`
    : `{total:${trackedItemsCount}}`;
};

const getBedType = bedGroups => {
  return bedGroups
    .map(
      ({ configuration }) =>
        configuration &&
        configuration
          .map(({ size }) => ROOM_BED_TYPE[size.toUpperCase()])
          .join(',')
    )
    .join('');
};

const productToCheckout = (
  hotel,
  filter,
  rate,
  room,
  isLoggedInUser,
  requiredSummary = false
) => {
  try {
    const {
      id,
      name,
      starRating,
      guestRating,
      guestRatingCount,
      allAmenities,
    } = hotel;

    const { id: roomId, otherAmenities, roomAmenities, occupancy } = room;

    const {
      baseRate,
      strikethroughPrice,
      bedGroups,
      amenities: rateAmenities,
      hotelChargedPropertyFee,
      totalInclusivePrice,
      cancelPenalties,
    } = rate;

    const {
      checkin,
      checkout,
      guests,
      occupancy: occupancies,
      roomsCount,
      countryCode,
    } = filter;

    const passengers = getTotalPassengers(guests);
    const hotelTags = getHotelTags(hotel);
    const hotelLengthOfStay = getDurationInDays(checkin, checkout);

    const AdultsAndChildren = `${guests?.adults || 0} | ${
      guests.children ? guests.children.length : 0
    }`;
    const [
      firstPenalty = { penalty_type: CANCELLATION_PENALTY_TYPES.FULL },
    ] = cancelPenalties;

    const refundableService = `${GA_BOOKING_PENALTY_TYPE[
      firstPenalty.penalty_type
    ] || 1}`;

    const roomView =
      otherAmenities &&
      otherAmenities.length &&
      otherAmenities.map(item => item.id).join('');

    const strikethroughPriceValue = strikethroughPrice
      ? Number(strikethroughPrice.value)
      : 0;

    const isWebView = getCurrentDeviceAgent().dId === ApiDeviceIds.isWebView;

    const {
      displayedRoomAmenities: displayedRoomAmenitiesWeb,
      displayedRateAmenities: displayedRateAmenitiesWeb,
    } = createDisplayAmenitiesWeb({
      rateAmenities,
      occupancy,
      otherAmenities,
      roomAmenities,
    });

    const {
      displayedRoomAmenities: displayedRoomAmenitiesMobile,
    } = createRoomLevelAmenitiesMobile({
      occupancy,
      otherAmenities,
      roomAmenities,
    });

    const displayedRoomAmenities = isWebView
      ? displayedRoomAmenitiesWeb
      : displayedRoomAmenitiesMobile;

    const displayedRateAmenities = isWebView
      ? displayedRateAmenitiesWeb
      : getMaxRateAmenities(rateAmenities);

    const quantity = requiredSummary
      ? { quantity: countNights(checkin, checkout) * occupancies.length }
      : {};

    return {
      id,
      name,
      position: 1,
      price: requiredSummary
        ? Number(totalInclusivePrice.value)
        : Number(baseRate.value),
      ...quantity,
      brand: 'Hotel SA', // LOB
      category: 'Hotel SA - Hotel',
      variant: '',
      dimension8: getBookingLeadDays(checkin),
      dimension9: Number(passengers),
      dimension10: Number(passengers),
      dimension60: countryCode,
      dimension67: checkin,
      dimension68: checkout,
      dimension69: hotelLengthOfStay,
      dimension70: roomsCount,
      dimension71: Number(starRating),
      dimension72: Number(guestRating),
      dimension76: `${refundableService}`,
      dimension77: `${roomView}`,
      dimension78: getBedType(bedGroups),
      dimension79: hotelTags,
      dimension80: hotelChargedPropertyFee,
      dimension93: roomId,
      dimension95: isLoggedInUser
        ? GA_VALUES.labels.logged_in
        : GA_VALUES.labels.non_loggedin,
      dimension103: guestRatingCount,
      dimension104: strikethroughPriceValue,
      dimension106: AdultsAndChildren,
      dimension105: '',
      dimension107: getDisplayedAmenitiesCode(allAmenities),
      dimension108: getDisplayedAmenitiesCode(displayedRoomAmenities),
      dimension109: getDisplayedAmenitiesCode(displayedRateAmenities),
      dimension110: percentPromotion(
        Number(baseRate.value),
        strikethroughPriceValue
      ),
    };
  } catch (error) {
    console.error(error);
    return {};
  }
};

const hotelDetailsImpressions = ({
  payload: { filter, result, isLoggedInUser },
  requiredAmenitiesCode = true,
}) => {
  try {
    const {
      checkin,
      checkout,
      roomsCount,
      guests,
      currency,
      location,
      locationCountryCode,
      locationType,
    } = filter;

    let amenities = {};

    if (requiredAmenitiesCode && result) {
      const [hotelDetailsDispatch] = result;
      const hotelDetailsInfo = hotelDetailsDispatch.payload;
      const { top_amenities } = hotelDetailsInfo.amenities;
      amenities = { hotelAmenities: getFullAmenitiesCode(top_amenities) };
    }

    return {
      checkindate: checkin,
      checkoutdate: checkout,
      destination: location,
      destinationcategory: locationType,
      destinationcountry: locationCountryCode,
      adult: guests?.adults,
      kid: guests.children.length,
      infant: guests.infants.length,
      lengthofstay: getDurationInDays(checkin, checkout),
      rooms: roomsCount,
      currency: currency,
      ...amenities,
      lineofbusiness: GA_VALUES.labels.lob,
      loginstatus: isLoggedInUser
        ? GA_VALUES.labels.logged_in
        : GA_VALUES.labels.non_loggedin,
    };
  } catch (error) {
    console.error(error);
    return {};
  }
};

const hotelRoomImpressions = (hotelId, room) => {
  const { rates, otherAmenities, roomAmenities, id: roomId } = room;

  const roomAmenitiesCode = getFullAmenitiesCode(roomAmenities);

  return rates.map(rate => {
    const hotelImpressions = hotelRateImpression(
      roomAmenitiesCode,
      otherAmenities,
      hotelId,
      roomId,
      rate
    );
    return getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.roomImpression,
      hotelId,
      hotelImpressions.label
    );
  });
};

const hotelRateImpression = (
  roomAmenitiesCode,
  otherAmenities,
  hotelId,
  roomId,
  {
    baseRate,
    strikethroughPrice,
    isNonRefundable,
    isFreeCancellation,
    amenities: rateAmenities,
    bedGroups,
    hotelChargedPropertyFee,
  }
) => {
  try {
    const priceAfterDiscount = Number(baseRate.value);
    const refundableService = isFreeCancellation ? 3 : isNonRefundable ? 1 : 2;
    const rateAmenitiesCode = getFullAmenitiesCode(rateAmenities);

    const roomView =
      otherAmenities &&
      otherAmenities.length &&
      otherAmenities.map(item => item.id);
    const bedType = getBedType(bedGroups);
    const percentDiscount = strikethroughPrice
      ? percentPromotion(priceAfterDiscount, strikethroughPrice.value)
      : 0;

    const strikethroughPriceValue = strikethroughPrice
      ? Number(strikethroughPrice.value)
      : 0;

    return {
      hotelId,
      label: `{ip:${priceAfterDiscount},cd110:${strikethroughPriceValue},cd104:${percentDiscount},cd76:${refundableService},cd77:${roomView},cd78:${bedType},cd80:${hotelChargedPropertyFee},cd93:${roomId},cd108:${roomAmenitiesCode},cd109:${rateAmenitiesCode}}`,
    };
  } catch (error) {
    console.error(error);
    return {};
  }
};

const getBookingLeadDays = checkin => differenceInDaysFromCurrentDate(checkin);

const getTotalPassengers = guests => {
  const passengers =
    (guests.adults || 0) + (guests.children ? guests.children.length : 0);
  return passengers;
};

const getGuestDetailExtraLabel = ({ isDesktopWeb, isBookerForm }) => {
  if (isBookerForm) {
    return GA_VALUES.labels.booker;
  }
  if (isDesktopWeb) {
    return GA_VALUES.labels.overall;
  }
  return GA_VALUES.labels.summary;
};

const creatMissingFieldLabel = ({
  allFieldsEmpty,
  extraLabel,
  numberOfEmptyFields,
  fieldName,
  isBooker,
  roomIndexLabel,
}) => {
  if (allFieldsEmpty) {
    return `${GA_VALUES.labels.allFields}|${
      isBooker ? 'Booker' : roomIndexLabel
    }|${GA_VALUES.labels.bookersInfo}`;
  }
  if (numberOfEmptyFields === 1) {
    return `${GA_VALUES.labels.oneField}|${
      isBooker ? 'Booker' : roomIndexLabel
    }|${GUEST_REQUIRED_FIELDS.FIELD_NAME_LABELS[fieldName]}`;
  }

  return `${GA_VALUES.labels.multipleFields}|${
    isBooker ? 'Bookerinfo' : roomIndexLabel
  }`;
};

const createErrorAction = ({ type, fieldName }) => {
  if (type === 'pattern') {
    if (fieldName === 'email') {
      return GA_VALUES.actions.emailError;
    }
    if (fieldName === 'mobileNumber') {
      return GA_VALUES.actions.mobileError;
    }
    return GA_VALUES.actions.alphabetsError;
  }
  return `${type}Error`;
};

export default {
  [types.HOTEL_GUEST_DETAILS_BOOKER_CLICK]: label =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.guest_detail_heatmap,
      GA_VALUES.actions.click,
      `${GA_VALUES.labels.bookersInfo}|${label.payload}`
    ),
  [types.HOTEL_GUEST_DETAILS_BOOKER_MOBILE_DONE_CLICK]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.guest_detail_heatmap,
      GA_VALUES.actions.click,
      GA_VALUES.labels.bookersInfoDone
    ),
  [types.HOTEL_GUEST_DETAILS_ROOMS_CLICK]: ({ payload: { label } }) =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.guest_detail_heatmap,
      GA_VALUES.actions.click,
      label
    ),
  [types.HOTEL_MOBILE_BACK_BUTTON]: ({ payload }) => {
    const { pageName, label } = payload;
    return getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      pageName
        ? `Hotel ${pageName} ${GA_VALUES.categories.heatmap}`
        : GA_VALUES.categories.heatmap,
      GA_VALUES.actions.click,
      label || payload
    );
  },
  [types.HOTEL_EDIT_SEARCH_BUTTON]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.heatmap,
      GA_VALUES.actions.click,
      'Edit Search - Hotel Search Result'
    ),
  [types.HOTEL_ITEM_DISCARDED]: ({ payload: { hotelId, hotelName, reason } }) =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      'Hotel SA List API Load Incompletion',
      hotelId || hotelName || 'hotel id is not available', // hotel id that failed to load.
      reason
    ),
  [types.HOTEL_DETAILED_REVIEWS]: () => {
    return getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.heatmap,
      GA_VALUES.actions.view,
      GA_VALUES.labels.hotelViewReviews
    );
  },
  [types.TRACK_HOTEL_ROOM_FILTER]: ({ payload: { filterName } }) => {
    return getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelRoomFilterSelect,
      GA_VALUES.actions.click,
      filterName
    );
  },
  [types.HOTEL_DETAILED_REVIEWS_PAGINATION]: ({ payload: { trackCount } }) => {
    return getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.heatmap,
      GA_VALUES.actions.click,
      'view reviews|' + trackCount
    );
  },
  [types.HOTEL_REVIEWS_CLICK]: () => {
    return getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.heatmap,
      GA_VALUES.actions.click,
      GA_VALUES.labels.hotelReviewView
    );
  },
  [types.HOTEL_LIST_IMPRESSIONS]: ({
    payload: { filter, hotels, isLoggedInUser, startIndex = 0 },
  }) => {
    try {
      const {
        checkin,
        checkout,
        roomsCount,
        guests,
        currency,
        sortBy,
        location,
        locationCountryCode,
        locationType,
      } = filter;

      const bookingInfo = {
        destination: location,
        destinationcategory: locationType,
        destinationcountry: locationCountryCode,
        checkindate: checkin,
        checkoutdate: checkout,
        lengthofstay: getDurationInDays(checkin, checkout),
        rooms: roomsCount,
        adult: guests?.adults,
        kid: guests.children ? guests.children.length : 0,
        infant: guests.infants ? guests.infants.length : 0,
        currency: currency,
        lineofbusiness: GA_VALUES.labels.lob,
        loginstatus: isLoggedInUser
          ? GA_VALUES.labels.logged_in
          : GA_VALUES.labels.non_loggedin,
      };

      const ecommerce = {
        event: GA_VALUES.events.productImpressions,
        ecommerce: {
          currencyCode: currency,
          location_id: filter.locationId,
          impressions: hotels.map((hotel, index) => {
            const product = hotelImpressionApp(
              hotel,
              startIndex + index + 1,
              filter,
              isLoggedInUser
            ); // todo: after adding the sorting on the backend check if the position still works
            return {
              list: FILTER_TO_HOTEL_SEARCH_RESULTS[sortBy],
              ...product,
            };
          }),
        },
      };

      return [bookingInfo, ecommerce];
    } catch (error) {
      console.error(error);
      return [];
    }
  },

  [types.HOTEL_PRODUCT_CLICK]: ({
    payload: { hotel, hotelIndex, filter, isLoggedInUser },
  }) => {
    try {
      const { currency, sortBy } = filter;

      const product = hotelImpressionApp(
        hotel,
        hotelIndex + 1,
        filter,
        isLoggedInUser
      );

      return {
        event: GA_VALUES.events.productClick,
        ecommerce: {
          currencyCode: currency,
          location_id: filter.locationId,
          click: {
            actionField: {
              list: FILTER_TO_HOTEL_SEARCH_RESULTS[sortBy],
            },
            products: [product],
          },
        },
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_SHOW_MORE_DESCRIPTION]: ({ payload }) => {
    const { id: hotelId } = payload;
    return getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.heatmap,
      hotelId,
      'Hotel descriptions - show more'
    );
  },
  [types.HOTEL_SHOW_MORE_POLICY]: ({ payload }) => {
    const { id: hotelId } = payload;
    return getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.heatmap,
      hotelId,
      'Hotel policies - show more'
    );
  },
  [types.HOTEL_DETAIL_IMPRESSION]: ({ payload }) =>
    hotelDetailsImpressions({ payload }),
  [types.HOTEL_GUEST_DETAILS_PAGE_LOADED]: ({ payload }) => {
    return hotelDetailsImpressions({
      payload,
      requiredAmenitiesCode: false,
    });
  },
  [types.HOTEL_ROOM_SELECTED]: ({
    payload: { hotelDetail, selectedRate, selectedRoom },
  }) => {
    try {
      const { otherAmenities, roomAmenities, id: roomId } = selectedRoom;
      const roomAmenitiesCode = getFullAmenitiesCode(roomAmenities);
      const { id: hotelId } = hotelDetail;

      const selectedRateImpression = hotelRateImpression(
        roomAmenitiesCode,
        otherAmenities,
        hotelId,
        roomId,
        selectedRate
      );
      return getGtmDataLayer(
        GA_VALUES.events.trackEventNoEcommerce,
        GA_VALUES.categories.roomSelect,
        hotelId,
        selectedRateImpression.label
      );
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_PRODUCT_TO_CHECKOUT]: ({
    payload: {
      memorizedHotel: hotel,
      memorizedFilter: filter,
      rate,
      room,
      isLoggedInUser,
    },
  }) => {
    try {
      const { currency } = filter;
      const product = productToCheckout(
        hotel,
        filter,
        rate,
        room,
        isLoggedInUser
      );

      return {
        event: GA_VALUES.events.productClick,
        ecommerce: {
          currencyCode: currency,
          click: {
            actionField: { list: 'Hotel Search Results - Recommendation' },
            products: [product],
          },
        },
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_GUEST_DETAILS_PROCEED_TO_CHECKOUT]: ({
    payload: {
      memorizedHotel: hotel,
      memorizedFilter: filter,
      rate,
      room,
      cleverTapId,
      isLoggedInUser,
    },
  }) => {
    try {
      const { currency } = filter;
      const product = productToCheckout(
        hotel,
        filter,
        rate,
        room,
        true,
        isLoggedInUser
      );

      return {
        event: GA_VALUES.events.addToCart,
        ecommerce: {
          currencyCode: currency,
          lineofbusiness: 'Hotels SA',
          add: {
            actionField: { list: 'Hotel Search Results - Recommendation' },
            products: [product],
          },
        },
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_GUEST_DETAILS_API_FAILED]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelSAGuestDetailError,
      GA_VALUES.actions.view,
      GA_VALUES.labels.apiFailed
    ),
  [types.HOTEL_GUEST_DETAILS_API_FAILED_SEARCH_AGAIN]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelSAGuestDetailError,
      GA_VALUES.actions.click,
      GA_VALUES.labels.apiFailedSearchAgain
    ),

  [types.HOTEL_GUEST_DETAILS_PRICE_CHANGED]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelSAGuestDetailError,
      GA_VALUES.actions.view,
      GA_VALUES.labels.priceChange
    ),
  [types.HOTEL_GUEST_DETAILS_PRICE_CHANGED_SELECT_BUTTON]: ({
    payload: { selectedNewRoom },
  }) =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelSAGuestDetailError,
      GA_VALUES.actions.click,
      `${GA_VALUES.labels.priceChange} - ${
        selectedNewRoom
          ? GA_VALUES.labels.selectNewRoom
          : GA_VALUES.labels.combo
      }`
    ),
  [types.HOTEL_GUEST_DETAILS_HOTEL_NOT_AVAILABLE]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelSAGuestDetailError,
      GA_VALUES.actions.view,
      GA_VALUES.labels.hotelNotAvailable
    ),
  [types.HOTEL_GUEST_DETAILS_HOTEL_NOT_AVAILABLE_SEARCH_AGAIN]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelSAGuestDetailError,
      GA_VALUES.actions.click,
      `${GA_VALUES.labels.hotelNotAvailable} - ${GA_VALUES.labels.selectNewRoom}`
    ),

  [types.HOTEL_GUEST_DETAILS_HAS_MISSING_FIELDS]: ({
    payload: { errorsFormGroup, isDesktopWeb, isBookerForm },
  }) => {
    try {
      let errorCount = 0;
      let fieldName;
      let emptyFieldIndex;
      let isBooker = false;

      const length = Object.keys(errorsFormGroup)?.length;

      const maxRequiredField = isBookerForm
        ? GUEST_REQUIRED_FIELDS.BOOKER_FORM.length
        : (length - 1) * GUEST_REQUIRED_FIELDS.TRAVELER_FORM.length +
          GUEST_REQUIRED_FIELDS.BOOKER_FORM.length;

      let roomFieldsFormsHaveError = false;

      Object.keys(errorsFormGroup).forEach((key, index) => {
        const { errors } = errorsFormGroup[key];
        if (!errors) return;
        const requiredFields =
          isBookerForm || index === length - 1
            ? GUEST_REQUIRED_FIELDS.BOOKER_FORM
            : GUEST_REQUIRED_FIELDS.TRAVELER_FORM;

        requiredFields.forEach(fieldKey => {
          if (
            errors[fieldKey]?.error &&
            errors[fieldKey]?.type === 'required'
          ) {
            if (!isBookerForm && index !== length - 1) {
              roomFieldsFormsHaveError = true;
            }
            emptyFieldIndex = index;
            fieldName = fieldKey;
            errorCount++;
            isBooker = isBookerForm || index === length - 1;
          }
        });
      });

      return (
        errorCount > 0 &&
        getGtmDataLayer(
          GA_VALUES.events.trackEventNoEcommerce,
          GA_VALUES.categories.hotelSAGuestDetailError,
          GA_VALUES.actions.fieldLeftEmpty,
          creatMissingFieldLabel({
            allFieldsEmpty:
              errorCount === maxRequiredField ||
              (!roomFieldsFormsHaveError &&
                errorCount === GUEST_REQUIRED_FIELDS.BOOKER_FORM.length),
            extraLabel: getGuestDetailExtraLabel({
              isDesktopWeb,
              isBookerForm: isBookerForm || !roomFieldsFormsHaveError,
            }),
            numberOfEmptyFields: errorCount,
            fieldName,
            isBooker,
            roomIndexLabel: `Room ${emptyFieldIndex + 1}`,
          })
        )
      );
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_GUEST_DETAILS_FORM_FIELDS_ERROR]: ({
    payload: { errorsFormGroup, isBookerForm },
  }) => {
    try {
      let errorsList = [];

      const length = Object.keys(errorsFormGroup)?.length;

      Object.keys(errorsFormGroup).forEach((key, index) => {
        const { errors } = errorsFormGroup[key];
        if (!errors) return;
        Object.keys(errors).forEach(errorKey => {
          if (
            errors[errorKey]?.error &&
            errors[errorKey]?.type !== 'required'
          ) {
            errorsList = [
              ...errorsList,
              {
                isBooker: isBookerForm || index === length - 1,
                fieldName: errorKey,
                roomIndexLabel: `Room ${index + 1} | ${length - 1}`,
                ...errors[errorKey],
              },
            ];
          }
        });
      });
      return (
        errorsList?.length > 0 &&
        errorsList.map(error => {
          const { type, fieldName, isBooker, roomIndexLabel } = error;
          return getGtmDataLayer(
            GA_VALUES.events.trackEventNoEcommerce,
            GA_VALUES.categories.hotelSAGuestDetailError,
            createErrorAction({ type, fieldName }),
            `${
              isBooker
                ? `${GUEST_REQUIRED_FIELDS.FIELD_NAME_LABELS[fieldName]} (Booker)`
                : `${GUEST_REQUIRED_FIELDS.FIELD_NAME_LABELS[fieldName]} (${roomIndexLabel})`
            }`
          );
        })
      );
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_BOOKING_CONFIRMATION_MANAGE_MY_BOOKING]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelConfirmationHeatmap,
      GA_VALUES.actions.click,
      GA_VALUES.labels.manageMyBooking
    ),
  [types.HOTEL_BOOKING_CONFIRMATION_LOADED]: ({
    payload: { booking, loginStatus },
  }) => {
    try {
      const {
        checkin,
        checkout,
        totalRooms,
        totalNights,
        currency,
        totalOccupancy,
      } = booking;

      const { adults, children = 0 } = totalOccupancy || {};
      const AdultsAndChildren = `${adults} | ${children}`;
      const data = {
        checkindate: checkin,
        checkoutdate: checkout,
        lengthofstay: totalNights,
        rooms: totalRooms,
        //AdultsAndChildren,
        adult: adults,
        kid: children,
        currency: currency,
        lineofbusiness: GA_LABELS.lob,
        loginstatus: loginStatus ? GA_LABELS.logged_in : GA_LABELS.non_loggedin,
      };

      return data;
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_BOOKING_CONFIRMATION_SUCCESS]: ({
    payload: { locationinfo, booking },
  }) => {
    try {
      const {
        location,
        locationType,
        destinationCountryName,
        hotelid,
        hotelname,
      } = locationinfo;
      const { totalOccupancy } = booking;
      const { adults, children = 0 } = totalOccupancy || {};
      return {
        destination: location,
        destinationcategory: locationType,
        destinationcountry: destinationCountryName,
        totalpax: adults + children,
        hotelid: hotelid,
        hotelname: hotelname,
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_BOOKING_CONFIRMATION_ECOMMERCE]: ({
    payload: { hotelContent, roomDetail, booking, status, customerId },
  }) => {
    try {
      const {
        id,
        name,
        guestRating,
        starRating,
        numberOfReview,
        displayAmenities,
      } = hotelContent;
      const {
        totalRooms,
        totalNights,
        checkin,
        checkout,
        totalInclusivePrice,
        bookingId,
        hotelChargedPropertyFee,
        cancelPenalties,
        totalOccupancy,
        amenities,
        paymentDetails,
      } = booking;
      const { adults, children = 0 } = totalOccupancy || {};
      const { id: roomId, otherAmenities, room_amenities } = roomDetail;

      const quantity = totalRooms * totalNights;
      const roomView =
        otherAmenities &&
        otherAmenities.length &&
        otherAmenities.map(item => item.id).join('');
      const [
        firstPenalty = { penalty_type: CANCELLATION_PENALTY_TYPES.FULL },
      ] = cancelPenalties;
      let breakfastProvision = BREAKFAST_PROVISION.NOT_PROVIDED;
      if (
        amenities &&
        amenities.find(e => e.name === BREAKFAST_PROVISION.INCLUDES_BREAKFAST)
      ) {
        breakfastProvision = BREAKFAST_PROVISION.PROVIDED;
      }
      let wifiProvision = WIFI_PROVISION.NOT_PROVIDED;
      if (
        amenities &&
        amenities.find(e => e.name === WIFI_PROVISION.FREE_WIFI)
      ) {
        wifiProvision = WIFI_PROVISION.PROVIDED;
      }
      const product = {
        id,
        name,
        price: Number(totalInclusivePrice.value),
        quantity,
        brand: 'Hotel SA',
        category: 'Hotel SA - Hotel',
        variant: '',
        dimension8: getBookingLeadDays(checkin),
        dimension9: adults + children,
        dimension10: adults + children,
        dimension67: checkin,
        dimension68: checkout,
        dimension69: totalNights,
        dimension70: totalRooms,
        dimension71: Number(starRating),
        dimension72: Number(guestRating),
        dimension74: breakfastProvision,
        dimension75: wifiProvision,
        dimension76: `${GA_BOOKING_PENALTY_TYPE[firstPenalty.penalty_type] ||
          1}`,
        dimension77: roomView,
        dimension78: roomView,
        dimension79: '', //Hotel Tags
        dimension80: hotelChargedPropertyFee
          ? Number(hotelChargedPropertyFee.amount.value)
          : 0,
        dimennion93: `${roomId}`,
        dimension103: numberOfReview,
        dimension104: '', //Hotel Strikethrough Price
        dimension106: adults + children,
        dimension107: getDisplayedAmenitiesCode(displayAmenities),
        dimension108: getDisplayedAmenitiesCode(room_amenities),
        dimension109: getDisplayedAmenitiesCode(amenities),
        dimension110: '', //Additional Room Price
      };
      let paymentMethod = '';
      if (paymentDetails && paymentDetails.length > 0) {
        for (let i = 0; i < paymentDetails.length; i++) {
          const paymentItem = paymentDetails[i];
          if (PAYMENT_METHOD_ECOMMERCE_GTM[paymentItem.payment_method]) {
            paymentMethod +=
              PAYMENT_METHOD_ECOMMERCE_GTM[paymentItem.payment_method];
            if (i < paymentDetails.length - 1) {
              paymentMethod += '|';
            }
          }
        }
      } else {
        paymentMethod = 'VI';
      }
      let coupon = '';
      if (paymentDetails && paymentDetails.length > 0) {
        for (let i = 0; i < paymentDetails.length; i++) {
          const paymentItem = paymentDetails[i];
          if (paymentItem.payment_method === 'DISCOUNT') {
            if (coupon !== '') {
              coupon += ',';
            }
            coupon += paymentItem.other_details.discountName;
          }
        }
      }
      const ecommerce = {
        currencyCode: totalInclusivePrice.currency,
        purchase: {
          actionField: {
            id: bookingId,
            affiliation: 'airasia Hotels SA',
            revenue: Number(totalInclusivePrice.value),
            paymentMethod,
            paymentStatus: status,
            coupon: coupon,
            list: 'Hotel Search Result-Recommendation',
            customerId,
          },
          products: [product],
        },
      };

      return {
        event: 'purchase',
        ecommerce: ecommerce,
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_BOOKING_CONFIRMATION_STATUS]: ({
    payload: { status, bookingId },
  }) =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelSABooking,
      bookingId,
      GA_BOOKING_STATUS_MAPPING[status] || ''
    ),

  [types.HOTEL_BOOKING_CONFIRMATION_ERROR]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelBookingConfirmationError,
      GA_VALUES.actions.view,
      GA_VALUES.labels.apiFailed
    ),

  [types.HOTEL_BOOKING_DETAILS_ERROR]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.hotelBookingDetailsError,
      GA_VALUES.actions.view,
      GA_VALUES.labels.apiFailed
    ),
  [types.HOTEL_TRACKING_SEEONAMAP_WEB]: () => {
    try {
      return getGtmDataLayer(
        GA_VALUES.events.trackEventNoEcommerce,
        GA_VALUES.categories.heatmapv2,
        GA_VALUES.actions.click,
        `See on a map`
      );
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_TRACKING_SHOWMAP_WEB]: () => {
    try {
      return getGtmDataLayer(
        GA_VALUES.events.trackEventNoEcommerce,
        GA_VALUES.categories.heatmapv2,
        GA_VALUES.actions.click,
        `Show map`
      );
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_TRACKING_MAP_SELECTAROOM_WEB]: (filter, hotel) => {
    try {
      const { name, id } = hotel.hotel.hotelDetail;
      const { currency, checkin: checkedIn, guests } = hotel.filter.hotel;
      const product = mapClick(filter, hotel, guests, checkedIn, name, id);
      return {
        event: GA_VALUES.events.productClick,
        ecommerce: {
          currency: currency,
          click: {
            actionField: {
              list: 'Hotel Search Results - Recommendation',
            },
            products: [product],
          },
        },
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.TRACK_MAP_VIEW_CLICK]: () => {
    try {
      return getGtmDataLayer(
        GA_VALUES.events.trackEventNoEcommerce,
        'Hotels SA|Hotel Select heatmap',
        GA_VALUES.actions.click,
        'view hotels on map'
      );
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  [types.HOTEL_LOGIN_MODAL_OPENED]: () =>
    getGtmDataLayer(
      GA_VALUES.events.trackEventNoEcommerce,
      GA_VALUES.categories.uhf_login,
      GA_VALUES.actions.view,
      GA_VALUES.labels.loginModal
    ),
  [types.ERROR_POP_ROOM]: ({ payload }) => {
    const event = payload['event'];
    const category = payload['eventDetails.category'];
    const action = payload['eventDetails.action'];
    const label = payload['eventDetails.label'];
    return getGtmDataLayer(event, category, action, label);
  },
};
